// @ts-check
// ── Your next move: the dashboard's last card ─────────────────────────────────
//
// _handoffs/ultimate-dashboard.md item 3, from the approved prototype (mockups/ultimate-dashboard-v3-2026-10-13.html:
// nextMoveCard) and Clayton's rulings: the LAST card on the dashboard, folded or open, and the fold remembered for
// him (coaches.settings.nextMoveFolded). Folded, its title row carries the bottleneck and "3 moves this week · Last
// week: 1 of 3 done"; open, the bottleneck with chips to the cards behind its numbers, this week's moves (each with
// how, a target, how FitOps will know, and Add to My Tasks in the same place on every move), last week's moves
// checked, and what's going well, folded.
//
// The server (coach-next-move.js, _shared/dashboard/next-move.js) makes every word, once a week; this file draws
// them and sends what he presses.
//
// ⚠️ ITS OWN CLOCK, like Needs you: the plan is weekly, so it loads when the dashboard shows and is more than 10
// minutes old, and when he comes back to the tab. A period pick never reloads it.
// ⚠️ THE PAGE REDRAWS ITS COLUMN (index.js draw): what he opened or pressed lives in S, and nmPaint draws it again.

import { callFunction } from '../api.js';
import { escHtml, showToast } from '../utils.js';
import { ICON, info } from './tiles.js';

const FRESH_MS = 10 * 60_000;
/** The cards a chip may scroll to (the server names one of these) */
const GOTO = new Set(['#dv-cl-h', '#dv-fl-h', '#dv-rn-h', '.ny-row']);

const svg = (/** @type {string} */ d) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const I = {
  tasks: svg('<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>'),
  // Each state has its own shape, never colour alone: a filled check, a half circle, a dashed circle, a dash
  done: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="var(--primary)"/><path d="M8 12.4l2.7 2.7L16.2 9.6" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  part: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5" fill="none" stroke="var(--primary)" stroke-width="1.8"/><path d="M12 3.5a8.5 8.5 0 010 17z" fill="var(--primary)"/></svg>',
  not: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-dasharray="3 2.6"/></svg>',
  dash: svg('<path d="M7 12h10"/>'),
};
const STATUS = { done: ['Done', I.done], part: ['Partly done', I.part], not: ['Not done', I.not], none: ['Nothing to check', I.dash], unknown: ['Can\'t tell', I.not] };

const S = {
  /** @type {any} */ data: null,
  /** @type {Promise<void>|null} */ loading: null,
  loadedAt: 0, failed: false, started: false,
  /** Refresh pressed: the plan is being made again */ busy: false,
  /** moves being added to My Tasks @type {Set<string>} */ adding: new Set(),
  /** the button to put the keyboard on after the next draw @type {string|null} */ focusNext: null,
  /** a fold he pressed: kept over any answer already on its way (final review 10/20) @type {boolean|null} */ folded: null,
};
/** An answer, with the fold he pressed kept. @param {any} body */
const withFold = (body) => (S.folded == null ? body : { ...body, folded: S.folded });
/** One press on its way at a time: two at once could each keep only their own move in My Tasks (final review 10/20) */
const pressing = () => S.busy || S.adding.size > 0;

function host() { return /** @type {HTMLElement|null} */ (document.querySelector('#dv-root .nm-row')); }
function showing() {
  const v = document.getElementById('view-dashboard');
  return !!v && !v.classList.contains('hidden') && document.visibilityState !== 'hidden';
}
/** A value inside a quoted attribute selector. @param {unknown} v */
const q = (v) => String(v == null ? '' : v).replace(/["\\]/g, '\\$&');

// ── Loading ──

function load() {
  if (S.loading) return S.loading;
  S.loading = (async () => {
    try {
      const body = await callFunction('coach-next-move', null, 'GET');
      if (!body || !Array.isArray(body.moves)) throw new Error('an answer without a plan');
      if (!pressing()) { S.data = withFold(body); S.failed = false; S.loadedAt = Date.now(); }
    } catch (err) {
      console.warn('[dashboard] Your next move did not load:', err instanceof Error ? err.message : err);
      if (!S.data) S.failed = true;
    } finally {
      S.loading = null;
      nmPaint();
    }
  })();
  return S.loading;
}

/** The dashboard is showing: start once, load when out of date, else draw what it has. Cheap. */
export function nmShow() {
  if (/** @type {any} */ (window)._appMember) return; // the dashboard is the owner's only
  if (!S.started) {
    S.started = true;
    document.addEventListener('click', onClick);
    const back = () => { if (showing() && Date.now() - S.loadedAt >= FRESH_MS) void load(); };
    document.addEventListener('visibilitychange', back);
    window.addEventListener('focus', back);
  }
  if (!S.data || Date.now() - S.loadedAt >= FRESH_MS) void load();
  else nmPaint();
}

// ── Drawing ──

/** @param {any} m @param {number} i */
function moveHtml(m, i) {
  const know = m.check === 'auto'
    ? `<span class="nm-know">${ICON.check}<span><b>FitOps sees this on its own.</b> ${escHtml(m.checkWords)}</span></span>`
    : `<span class="nm-know">${I.tasks}<span><b>You check it off in My Tasks.</b>${m.checkWords ? ` ${escHtml(m.checkWords)}` : ''}</span></span>`;
  const adding = S.adding.has(m.id);
  const btn = m.added
    ? `<button type="button" class="nm-b nm-add is-in" data-nm-tasks="${escHtml(m.id)}" aria-label="In My Tasks: ${escHtml(m.title)}. Open My Tasks">${ICON.check}In My Tasks</button>`
    : `<button type="button" class="nm-b nm-add" data-nm-add="${escHtml(m.id)}"${pressing() ? ' disabled' : ''} aria-label="Add to My Tasks: ${escHtml(m.title)}">${adding ? 'Adding…' : 'Add to My Tasks'}</button>`;
  return `<li class="nm-move">
    <span class="nm-n" aria-hidden="true">${i + 1}</span>
    <div class="nm-mb">
      <p class="nm-mt"><b>${escHtml(m.title)}</b>${m.carried ? ' <span class="nm-tag">From last week</span>' : ''}</p>
      <p class="nm-how">${escHtml(m.how)}</p>
      <p class="nm-target"><b>Target:</b> ${escHtml(m.targetWords)}</p>
      <div class="nm-foot">${know}</div>
    </div>
    ${btn}</li>`;
}

/** @param {any} x */
function lastHtml(x) {
  const [label, icon] = /** @type {any} */ (STATUS)[x.status] || STATUS.unknown;
  return `<li>
    <span class="nm-st">${icon}<b>${label}</b></span>
    <p class="nm-lt">${escHtml(x.title)}</p>
    <p class="nm-res">${x.lead ? `<b>${escHtml(x.lead)}</b> ` : ''}${escHtml(x.text)}</p>
    ${x.source ? `<p class="nm-src">${escHtml(x.source)}</p>` : ''}</li>`;
}

/** Draw the card into the dashboard's last slot (index.js puts it there). */
export function nmPaint() {
  const h = host();
  if (!h) return;
  const D = S.data;
  if (!D) {
    h.innerHTML = S.failed ? `<section class="dv-card dv-s12 nm"><p class="nm-note" role="alert">Your next move didn't load. <button type="button" class="dv-link" data-nm-retry>Try again</button></p></section>` : '';
    return;
  }
  const open = !D.folded;
  const tools = open
    ? `<span class="nm-made">${escHtml(D.made)}</span><button type="button" class="nm-b" data-nm-refresh${pressing() ? ' disabled' : ''}>${S.busy ? 'Reading your numbers' : 'Refresh'}</button>`
    : `<span class="nm-made">${escHtml(D.summary)}</span>`;
  const body = S.busy
    ? '<div class="nm-busy" role="status">Reading this dashboard\'s numbers and last week\'s moves…</div>'
    : `<div class="nm-bn">
        <span class="nm-lab">The bottleneck</span>
        <p class="nm-bt"><b>${escHtml(D.headline)}</b> ${escHtml(D.text)}</p>
        ${D.chips.length ? `<div class="nm-chips">${D.chips.filter((/** @type {any} */ c) => GOTO.has(c.goto)).map((/** @type {any} */ c) => `<button type="button" class="nm-chip" data-nm-goto="${escHtml(c.goto)}">${escHtml(c.text)}${ICON.chevR}</button>`).join('')}</div>` : ''}
      </div>
      <div class="nm-grid${D.last ? '' : ' is-one'}">
        <div class="nm-col">
          <h3 class="nm-h">This week</h3>
          ${D.moves.length ? `<ol class="nm-moves">${D.moves.map(moveHtml).join('')}</ol>` : '<p class="nm-note">Nothing to add this week.</p>'}
        </div>
        ${D.last ? `<div class="nm-col nm-last">
          <h3 class="nm-h">Last week <span class="nm-sub">given ${escHtml(D.last.given)}</span></h3>
          <ul class="nm-lw">${D.last.items.map(lastHtml).join('')}</ul>
        </div>` : ''}
      </div>
      ${D.good.length ? `<details class="nm-good"><summary>What's going well (${D.good.length})</summary><ul>${D.good.map((/** @type {string} */ g) => `<li>${I.done}<span>${escHtml(g)}</span></li>`).join('')}</ul></details>` : ''}`;
  // Keep the keyboard where it was: the card is drawn again after every press. ⚠️ A press that finishes later never
  // pulls the keyboard (or the page) back to the card once he has gone elsewhere (final review 10/20).
  const active = /** @type {HTMLElement|null} */ (document.activeElement);
  const keep = active && h.contains(active) ? focusKey(active) : null;
  const free = !active || active === document.body || h.contains(active);
  h.innerHTML = `<section class="dv-card dv-s12 nm${open ? '' : ' is-closed'}" aria-labelledby="nm-h">
    <div class="dv-card-head">
      <div class="dv-head-l"><h2 class="dv-title" id="nm-h">Your next move</h2>${info('nextMove')}</div>
      ${open ? '' : `<p class="nm-sum">${escHtml(D.headline)}</p>`}
      <div class="nm-tools">${tools}<button type="button" class="nm-b nm-toggle" data-nm-toggle aria-expanded="${open}" aria-controls="nm-body">${open ? 'Hide the plan' : 'Show the plan'}${ICON.chev}</button></div>
    </div>
    <div id="nm-body"${open ? '' : ' hidden'}>${body}</div>
  </section>`;
  const sel = (free ? S.focusNext : null) || keep; S.focusNext = null;
  if (sel) /** @type {HTMLElement|null} */ (h.querySelector(sel))?.focus({ preventScroll: true });
}

/** A selector that finds the same button after a redraw. @param {HTMLElement} el */
function focusKey(el) {
  for (const a of ['data-nm-add', 'data-nm-toggle', 'data-nm-refresh', 'data-nm-goto', 'data-nm-tasks']) {
    const v = el.getAttribute(a);
    if (v != null) return v ? `[${a}="${q(v)}"]` : `[${a}]`;
  }
  return null;
}

// ── What he presses ──

/** @param {MouseEvent} e */
function onClick(e) {
  const t = /** @type {HTMLElement|null} */ (e.target instanceof Element ? e.target.closest('#dv-root .nm-row button') : null);
  if (!t) return;
  const ds = t.dataset;
  if (ds.nmRetry !== undefined) { S.failed = false; nmPaint(); void load(); return; }
  if (ds.nmToggle !== undefined) { void fold(!(S.data && S.data.folded)); return; }
  if (ds.nmRefresh !== undefined) { void refresh(); return; }
  if (ds.nmAdd) { void add(ds.nmAdd); return; }
  if (ds.nmTasks) { openTasks(); return; }
  if (ds.nmGoto) goTo(ds.nmGoto);
}

/** Fold or open the card, and remember it for him. @param {boolean} folded */
async function fold(folded) {
  if (!S.data) return;
  S.folded = folded;
  S.data = { ...S.data, folded };
  S.focusNext = '[data-nm-toggle]';
  nmPaint();
  try {
    await callFunction('save-coach-settings', { settings: { nextMoveFolded: folded } }, 'POST');
  } catch (err) {
    console.warn('[dashboard] the fold was not saved:', err instanceof Error ? err.message : err);
  }
}

async function refresh() {
  if (pressing()) return;
  S.busy = true;
  S.focusNext = '[data-nm-refresh]';
  nmPaint();
  try {
    const body = await callFunction('coach-next-move', { action: 'refresh' }, 'POST');
    if (!body || !Array.isArray(body.moves)) throw new Error('an answer without a plan');
    S.data = withFold(body); S.loadedAt = Date.now();
  } catch (err) {
    showToast('Your next move didn\'t refresh. Try again.', 'error');
  } finally {
    S.busy = false;
    S.focusNext = '[data-nm-refresh]';
    nmPaint();
  }
}

/** @param {string} id */
async function add(id) {
  if (pressing()) return;
  S.adding.add(id);
  S.focusNext = `[data-nm-add="${q(id)}"]`;
  nmPaint();
  try {
    const body = await callFunction('coach-next-move', { action: 'add', moveId: id }, 'POST');
    if (!body || !Array.isArray(body.moves)) throw new Error('an answer without a plan');
    S.data = withFold(body); S.loadedAt = Date.now();
    showToast('Added to My Tasks');
  } catch (err) {
    showToast('That move wasn\'t added to My Tasks. Try again.', 'error');
  } finally {
    S.adding.delete(id);
    const at = S.data ? S.data.moves.findIndex((/** @type {any} */ x) => x.id === id) : -1;
    S.focusNext = at >= 0 && S.data.moves[at].added ? `[data-nm-tasks="${q(id)}"]` : `[data-nm-add="${q(id)}"]`;
    nmPaint();
  }
}

/** "In My Tasks": open the My Tasks drawer (coach-tasks.js), if it is not open already. */
function openTasks() {
  const drawer = document.getElementById('tasks-drawer');
  if (drawer && !drawer.classList.contains('hidden')) return;
  /** @type {any} */ (window).coachTasks?.toggleDrawer?.();
}

/** A chip: scroll to the card behind its number. @param {string} sel */
function goTo(sel) {
  if (!GOTO.has(sel)) return;
  const el = /** @type {HTMLElement|null} */ (document.querySelector(`#dv-root ${sel}`));
  if (!el) return;
  const still = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
  if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
  el.focus({ preventScroll: true });
}

/** For tests: forget everything and stop listening. */
export function __resetNextMoveForTests() {
  document.removeEventListener('click', onClick);
  Object.assign(S, { data: null, loading: null, loadedAt: 0, failed: false, started: false, busy: false, adding: new Set(), focusNext: null, folded: null });
}
